
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, Inject, Optional, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { PlotlyModule } from 'angular-plotly.js';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { take, switchMap, of, tap, Observable } from 'rxjs';
import { selectActiveTabDetails, selectSidenavDataByTabIdAndKey, selectTabById } from '../../../../../../state/selectors';
import { AppState, Iparams, Tab } from '../../../../../../state/app.state';
import { IKeywords, ITechnologyFootprint } from '../../../../../../core/models/results.model';
import { loadSidenavData } from '../../../../../../state/action';
import { NgxPaginationModule } from 'ngx-pagination';
import { NullableNumberPipe } from 'src/app/shared/pipes/nullable-number.pipe';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpResponse } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-innovation-activity',
  standalone: true,
  imports: [CommonModule, PlotlyModule, NgxPaginationModule, NullableNumberPipe, MatTooltipModule],
  templateUrl: './innovation-activity.component.html',
  styleUrl: './innovation-activity.component.css'
})
export class InnovationActivityComponent implements OnInit, OnChanges {
  // Popup support inputs
  @Input() isPopup: boolean = false;
  @Input() popupCompanyName?: string;
  @Input() popupTheme?: string;

  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() search_type: string | null = null;

  tabs$!: Observable<Tab | undefined>;
  showPlot: boolean = false;
  constructor(
    private store: Store<AppState>, 
    private apiService: GeneralInnovationActivityResultsService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData?: any,
    @Optional() private dialogRef?: MatDialogRef<InnovationActivityComponent>
  ) {
    // Handle popup data if provided
    if (this.dialogData) {
      this.isPopup = this.dialogData.isPopup || false;
      this.popupCompanyName = this.dialogData.popupCompanyName;
      this.popupTheme = this.dialogData.popupTheme;
      this.tabId = this.dialogData.tabId;
      this.query = this.dialogData.query;
      this.entityids = this.dialogData.entityids;
      this.tab_name = this.dialogData.popupCompanyName;
    }
  }
  showTechnologyFootprint: boolean = false;
  patentActivityPlot: any;
  showQueryBox: boolean = false;

  keywords: IKeywords[] = [];
  paginator_1_page: number = 1;
  keywords_data_loading_failed: boolean = false;
  showNoDataFound: boolean = false;
  showKeywordsNoDataFound: boolean = false;

  ngOnInit(): void {
    // Note: Data fetching is now handled in ngOnChanges for better input handling
  }

  // Method to manually trigger data fetching (can be called from parent)
  public initializePopupData(): void {
    if (this.isPopup) {
      this.patentActivityPopup();
      this.topKeywordsPopup();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if any popup-related inputs have changed
    if (changes['isPopup'] || changes['popupCompanyName'] || changes['popupTheme'] || changes['tabId'] || changes['query'] || changes['entityids']) {
      this.tabs$ = this.store.select(selectActiveTabDetails);
      this.patent_activity_data_loading_failed = false;
      this.showPlot = false;
      this.patentActivityData = [];
      this.keywords_data_loading_failed = false;
      this.keywords = [];
      this.paginator_1_page = 1;
      this.showNoDataFound = false;
      this.showKeywordsNoDataFound = false;
      this.showCompanyTypesNoDataFound = false;
      this.showCompanyTypesPlot = false;
      this.companyTypesData = [];
      this.company_types_data_loading_failed = false;
      this.showCompanyTypesPlot = false;
      this.companyTypesGraph = [];

      // Check if we're now in popup mode and have all required inputs
      if (this.isPopup && this.query && this.entityids && this.tabId) {
        this.patentActivityPopup();
        this.topKeywordsPopup();
      } else if (!this.isPopup) {
        this.patentActivity();
        this.topKeywords();
        // this.getCompanyTypesData();
      }
    }
  }

  // Toggle query box
  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  // Close popup method
  closePopup(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  // Popup-specific data fetching methods
  patentActivityPopup() {
    this.apiService.getInnovationActivity(this.query, this.entityids, this.tabId).subscribe({
      next: (response) => {
        if (response.status === 204) {
          // No data found
          this.patentActivityData = [];
          this.patent_activity_data_loading_failed = false;
          this.showPlot = false;
          this.showNoDataFound = true; // New variable to track 204 responses
          this.cdr.detectChanges();
        } else if (response.body?.payload != null && response.body.payload.results && response.body.payload.results.length > 0) {
          this.patentActivityData = response.body.payload.results;
          this.plotActivity();
          this.patent_activity_data_loading_failed = false;
          this.showNoDataFound = false;
          this.cdr.detectChanges();
        } else {
          // Handle case where no data is found (empty results)
          this.patentActivityData = [];
          this.patent_activity_data_loading_failed = false;
          this.showPlot = false;
          this.showNoDataFound = true;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.patent_activity_data_loading_failed = true;
        this.showNoDataFound = false;
        this.cdr.detectChanges();
      }
    });
  }

  topKeywordsPopup() {
    this.paginator_1_page = 1; // Reset page number
    this.apiService.getKeywords(this.query, this.entityids, this.tabId).subscribe({
      next: (response) => {
        if (response.status === 204) {
          // No data found
          this.keywords = [];
          this.keywords_data_loading_failed = false;
          this.showKeywordsNoDataFound = true; // New variable to track 204 responses
          this.cdr.detectChanges();
        } else if (response.body?.payload != null && response.body.payload.results && response.body.payload.results.length > 0) {
          this.keywords = response.body.payload.results;
          this.keywords_data_loading_failed = false;
          this.showKeywordsNoDataFound = false;
          this.cdr.detectChanges();
        } else {
          // Handle case where no data is found (empty results)
          this.keywords = [];
          this.keywords_data_loading_failed = false;
          this.showKeywordsNoDataFound = true;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.keywords_data_loading_failed = true;
        this.showKeywordsNoDataFound = false;
        this.cdr.detectChanges();
      }
    });
  }

  topKeywords() {
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'top_keywords')).pipe(
      take(1),
      switchMap((data) => {
        if (!data) {
          return this.apiService.getKeywords(this.query, this.entityids, this.tabId).pipe(
            tap((response) => {
              if (response.status === 204) {
                // No data found
                this.keywords = [];
                this.keywords_data_loading_failed = false;
                this.showKeywordsNoDataFound = true;
                return;
              }

              if (response.body?.payload.tabId != this.tabId) {
                this.store.dispatch(loadSidenavData({
                  tabId: response.body!.payload.tabId,
                  sidenavKey: 'top_keywords',
                  data: response.body!.payload.results
                }));
                return;
              }
              if (response.body?.payload != null && response.body.payload.results && response.body.payload.results.length > 0) {
                this.store.dispatch(loadSidenavData({
                  tabId: this.tabId,
                  sidenavKey: 'top_keywords',
                  data: response.body.payload.results
                }));

                this.keywords = response.body.payload.results;
                this.showKeywordsNoDataFound = false;
              } else {
                // Handle case where no data is found (empty results)
                this.keywords = [];
                this.keywords_data_loading_failed = false;
                this.showKeywordsNoDataFound = true;
              }

            })
          );
        } else {
          this.keywords = data;
          this.keywords_data_loading_failed = false;
          return of();
        }
      })
    ).subscribe({
      next: () => {
      },
      error: (err) => {
        this.keywords_data_loading_failed = true;

      }
    });
  }
  patent_activity_data_loading_failed: boolean = false;
  patentActivityData: any = [];
  patentActivity() {
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'patent_activity')).pipe(
      take(1),
      switchMap((data) => {
        if (!data) {
          return this.apiService.getInnovationActivity(this.query, this.entityids, this.tabId).pipe(
            tap((response) => {
              if (response.status === 204) {
                // No data found
                this.patentActivityData = [];
                this.patent_activity_data_loading_failed = false;
                this.showPlot = false;
                this.showNoDataFound = true;
                return;
              }

              if (response.body?.payload.tabId != this.tabId) {
                this.store.dispatch(loadSidenavData({
                  tabId: response.body!.payload.tabId,
                  sidenavKey: 'patent_activity',
                  data: response.body!.payload.results
                }));
                return;
              }
              if (response.body?.payload != null && response.body.payload.results && response.body.payload.results.length > 0) {
                this.store.dispatch(loadSidenavData({
                  tabId: this.tabId,
                  sidenavKey: 'patent_activity',
                  data: response.body.payload.results
                }));

                this.patentActivityData = response.body.payload.results;
                this.plotActivity();
                this.showNoDataFound = false;
              } else {
                // Handle case where no data is found (empty results)
                this.patentActivityData = [];
                this.patent_activity_data_loading_failed = false;
                this.showPlot = false;
                this.showNoDataFound = true;
              }

            })
          );
        } else {
          this.patent_activity_data_loading_failed = false;
          this.patentActivityData = data;
          this.plotActivity();
          return of();
        }
      })
    ).subscribe({
      next: () => {
      },
      error: (err) => {
        this.patent_activity_data_loading_failed = true;

      }
    });
  }


  company_types_data_loading_failed: boolean = false;
  companyTypesData: any = [];
  showCompanyTypesNoDataFound: boolean = false;
  showCompanyTypesPlot: boolean = false;
  companyTypesGraph: any = [];
  getCompanyTypesData() {
    this.showCompanyTypesPlot=false;
    this.store.select(selectSidenavDataByTabIdAndKey(this.tabId, 'company_types_graph_data')).pipe(
      take(1),
      switchMap((data) => {
        if (!data) {
          return this.apiService.getCompanyTypesActivity(this.query, this.tabId).pipe(
            tap((response) => {
              if (response.status === 204) {
                // No data found
                this.companyTypesData = [];
                this.company_types_data_loading_failed = false;
                this.showCompanyTypesPlot = false;
                this.showCompanyTypesNoDataFound = true;
                return;
              }

              if (response.body?.payload.tabId != this.tabId) {
                this.store.dispatch(loadSidenavData({
                  tabId: response.body!.payload.tabId,
                  sidenavKey: 'company_types_graph_data',
                  data: response.body!.payload.data
                }));
                return;
              }
              if (response.body?.payload != null && response.body.payload.data && response.body.payload.data.length > 0) {
                this.store.dispatch(loadSidenavData({
                  tabId: this.tabId,
                  sidenavKey: 'company_types_graph_data',
                  data: response.body.payload.data
                }));

                this.companyTypesData = response.body.payload.data;
                this.showCompanyTypesNoDataFound = false;
                this.plotCompanyTypes();
              } else {
                // Handle case where no data is found (empty results)
                this.companyTypesData = [];
                this.company_types_data_loading_failed = false;
                this.showCompanyTypesPlot = false;
                this.showCompanyTypesNoDataFound = true;
              }
            })
          );
        } else {
          this.company_types_data_loading_failed = false;
          this.companyTypesData = data;
          this.plotCompanyTypes();
          return of();
        }
      })
    ).subscribe({
      next: () => {
      },
      error: (err) => {
        this.company_types_data_loading_failed = true;

      }
    });
  }


  plotActivity() {
    const countByYearData = this.patentActivityData;
    const years = countByYearData?.map((item: { ype: any }) => item.ype);
    const counts = countByYearData?.map((item: { invention_count: any }) => item.invention_count);

    this.patentActivityPlot = {
      data: [{
        x: years,
        y: counts,
        type: 'bar',
        marker: {
          'color': '#a2c613'
        }
      }],
      layout: {
        annotations: [
          {
            xref: 'paper',
            yref: 'paper',
            x: 0.5,
            y: 1.04,
            xanchor: 'center',
            yanchor: 'bottom',
            text: '* The latest year is incomplete and considered year-to-date. ',
            showarrow: false,
            font: {
              size: 12,
              color: 'gray'
            }
          }],
        yaxis: { title: 'Invention Count' },
        xaxis: { title: 'Year', tickmode: 'linear', tick0: years![0], dtick: 1 },
        // width: 500,
        showlegend: false,
        legend: { "orientation": "h" }
      }
    };
    this.showPlot = true;
  }

  plotCompanyTypes() {
const traces = JSON.parse(JSON.stringify(this.companyTypesData));

  this.showCompanyTypesPlot=false;
  const years = traces[0]?.x || [];

  const layout = {
    title: {
      text: 'Innovation Activity by Company Type',
      font: {
        size: 20,
        family: 'Arial, sans-serif',
        color: 'black',
        weight: 'bold'
      }
    },
    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: 0.5,
        y: 1.04,
        xanchor: 'center',
        yanchor: 'bottom',
        text: '* The latest year may be incomplete.',
        showarrow: false,
        font: {
          size: 12,
          color: 'gray'
        }
      }
    ],
    barmode: 'stack',
    xaxis: { title: 'Year', tickmode: 'linear', tick0: years[0], dtick: 1 },
    yaxis: { title: 'Percentage %' },
    showlegend: true,
    legend: {
      orientation: 'h',
      y: -0.5,
      itemsizing: 'constant',
      font: { size: 10 }
    }
  };

  this.companyTypesGraph = {
    data: traces,
    layout: layout
  };
  this.showCompanyTypesPlot = true;
}





}
